import * as React from 'react';
import { useForm } from 'react-hook-form';
import { Loader } from 'lucide-react';
import { Form, FormField } from 'components/ui/form';
import { InputGs, SelectGs } from 'components/ui/Forms';
import { Button } from 'components/ui/ButtonGs';
import type { ViagensBillingFilters } from '../../types';
import { useViagensBillingSearchParams } from 'hooks/store/viagens-billing';

type Props = {
  onSubmitFilters: (filters: ViagensBillingFilters) => void;
  onResetFilters: () => void;
  filterLoadingAction?: 'filter' | 'reset' | null;
};

type FormData = {
  billingId: string;
  createdAtStart: string;
  createdAtEnd: string;
  approvalStatus: string;
};

const approvalStatusOptions = [
  { name: 'Pendente', value: 'pending' },
  { name: 'Aprovada', value: 'approved' },
  { name: 'Reprovada', value: 'rejected' },
  { name: 'Estornada', value: 'estornada' },
];

export const ViagensBillingFilter = ({
  onSubmitFilters,
  onResetFilters,
  filterLoadingAction,
}: Props) => {
  const { filters } = useViagensBillingSearchParams({ persisted: true });
  const form = useForm<FormData>({
    defaultValues: {
      billingId: filters.billingId ?? '',
      createdAtStart: filters.createdAtStart ?? '',
      createdAtEnd: filters.createdAtEnd ?? '',
      approvalStatus: filters.approvalStatus ?? '',
    },
  });

  const buildFilters = React.useCallback((formData: FormData) => {
    return {
      billingId: formData.billingId?.trim() || undefined,
      createdAtStart: formData.createdAtStart || undefined,
      createdAtEnd: formData.createdAtEnd || undefined,
      approvalStatus: (formData.approvalStatus || undefined) as
        | 'pending'
        | 'approved'
        | 'rejected'
        | 'estornada'
        | undefined,
    };
  }, []);

  const resetFilters = React.useCallback(() => {
    form.reset({
      billingId: '',
      createdAtStart: '',
      createdAtEnd: '',
      approvalStatus: '',
    });
    onResetFilters();
  }, [form, onResetFilters]);

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((data) =>
          onSubmitFilters(buildFilters(data))
        )}
        className="w-full"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <FormField
            control={form.control}
            name="billingId"
            render={({ field }) => (
              <InputGs {...field} type="number" label="ID da fatura" />
            )}
          />
          <FormField
            control={form.control}
            name="createdAtStart"
            render={({ field }) => (
              <InputGs {...field} type="date" label="Criada de" />
            )}
          />
          <FormField
            control={form.control}
            name="createdAtEnd"
            render={({ field }) => (
              <InputGs {...field} type="date" label="Criada até" />
            )}
          />
          <FormField
            control={form.control}
            name="approvalStatus"
            render={({ field }) => (
              <SelectGs
                {...field}
                onValueChange={field.onChange}
                label="Status"
                content={approvalStatusOptions}
              />
            )}
          />
        </div>

        <div className="flex items-center mt-6 gap-4">
          <Button.Root
            type="button"
            size="sm"
            variant="secondary"
            disabled={Boolean(filterLoadingAction)}
            onClick={resetFilters}
          >
            <span className="relative grid place-items-center">
              <span
                className={filterLoadingAction === 'reset' ? 'invisible' : ''}
              >
                Limpar filtros
              </span>
              {filterLoadingAction === 'reset' && (
                <Loader className="absolute h-4 w-4 animate-spin" />
              )}
            </span>
          </Button.Root>

          <Button.Root
            type="submit"
            size="sm"
            variant="primary"
            disabled={Boolean(filterLoadingAction)}
          >
            <span className="relative grid place-items-center">
              <span
                className={filterLoadingAction === 'filter' ? 'invisible' : ''}
              >
                Filtrar
              </span>
              {filterLoadingAction === 'filter' && (
                <Loader className="absolute h-4 w-4 animate-spin" />
              )}
            </span>
          </Button.Root>
        </div>
      </form>
    </Form>
  );
};
